/**
 * THE ONE APPROVALS WORKLIST (CTS1, Approvals.jsx — the owner's 18 Sep
 * instruction: learn from the old pages). Everything waiting on a reviewer,
 * in one place: pending challans and draft orders, each a card with its
 * contents and its two decisions. OLDEST FIRST — the old page called that
 * fairness and enforced it in the sort, so nobody's truck waits because a
 * newer, easier row sat on top.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, EmptyState, Loading, Money, Pill, Seg, Serial, TextArea } from '../ui.js';
import { Page } from '../shell.js';
import { count, date, money, when, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('approvals')!.messages;

function daysAgo(iso: string): string {
  const then = new Date(iso + 'T00:00:00').getTime();
  const d = Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
  return d === 0 ? 'today' : d === 1 ? '1 day' : `${d} days`;
}

interface Decided { challanId: number; type: string; customerName: string; action: 'approved' | 'rejected' }

export function Approvals({ session }: { session: Session }) {
  return (
    <Page title="Approvals" sub="Submissions waiting for review, oldest first. Approve to apply; reject to send it back in writing.">
      <ApprovalsPanel session={session} />
    </Page>
  );
}

/** 10 Oct — the worklist without its page frame, so Movements can carry it as its Waiting tab.
 *  0173 — rebuilt on the approvals book: every kind a person may decide, in one list, oldest
 *  first; what they raised themselves is shown without buttons and says why; and the book of
 *  what was decided, newest first, under it. */
export function ApprovalsPanel({ session }: { session: Session }) {
  const [area, setArea] = useState<'' | 'order' | 'challan' | 'purchase_order' | 'quarantine_release' | 'quotation'>('');
  const [decided, setDecided] = useState<Decided[]>([]);
  const book = useAsk(() => api.view('approvals.book', { limit: 50 }), [], MESSAGES);
  const pending = book.data?.pending ?? [];
  // loads first: a truck waits on a load; an order, a purchase or a hold can wait an hour
  const kinds = [
    { value: 'challan', label: 'Loads' }, { value: 'order', label: 'Orders' },
    { value: 'purchase_order', label: 'Purchases' }, { value: 'quarantine_release', label: 'Quarantine' }, { value: 'quotation', label: 'Quotations' },
  ].map((k) => ({ ...k, n: pending.filter((p) => p.kind === k.value).length }));
  const shownKind = area || (kinds.find((k) => k.n > 0)?.value ?? 'challan');
  const rows = pending.filter((p) => p.kind === shownKind);
  const oldest = rows[0];

  return (
    <>
      <div className="toolbar" style={{ alignItems: 'center' }}>
        <Seg name="area" value={shownKind} onChange={(v) => setArea(v as typeof area)}
             options={kinds.filter((k) => book.data ? book.data.mayDecide[k.value as keyof typeof book.data.mayDecide] : true).map((k) => ({ value: k.value, label: `${k.label} (${count(k.n)})` }))} />
        {oldest?.requestedOn && daysAgo(oldest.requestedOn) !== 'today' ? <span className="muted">the oldest has waited {daysAgo(oldest.requestedOn)}</span> : null}
      </div>

      {decided.map((d) => (
        <Banner key={`${d.action}-${d.challanId}`} kind={d.action === 'approved' ? 'good' : 'warn'}
                title={`${d.type === 'dispatch' ? 'Dispatch' : 'Return'} for ${d.customerName} — ${d.action}.`}
                detail={d.action === 'approved'
                  ? (d.type === 'dispatch' ? 'The load may depart. The links below carry on from here.' : 'The cylinders are back; the receipt challan is issued.')
                  : 'The operator will see the reason on the register.'} />
      ))}
      {decided.length ? (
        <div className="toolbar">
          {decided.filter((d) => d.action === 'approved' && d.type === 'dispatch').map((d) => (
            <Button key={d.challanId} kind="primary" onClick={() => go(`#/dispatch/${d.challanId}`)}>Record the departure — {d.customerName}</Button>
          ))}
          {decided.map((d) => (
            <Button key={`reg-${d.challanId}`} onClick={() => go(`#/challans/${d.challanId}`)}>On the register: {d.customerName}</Button>
          ))}
          <Button small onClick={() => setDecided([])}>Clear</Button>
        </div>
      ) : null}

      {book.loading && !book.data ? <Card><Loading what="what waits" /></Card>
        : book.error ? <Card><Banner kind="error" title={book.error.title} {...(book.error.detail ? { detail: book.error.detail } : {})} /></Card>
        : rows.length === 0
          ? <Card><EmptyState title={`Nothing is waiting under ${kinds.find((k) => k.value === shownKind)?.label.toLowerCase() ?? 'this heading'}.`}
                              detail={pending.length > 0 ? `${count(pending.length)} item${pending.length === 1 ? ' waits' : 's wait'} under the other headings.` : 'Nothing is waiting for a decision right now.'} /></Card>
          : rows.map((raw) => {
              // the rule binds role holders other than the CEO; a developer or a one-person company decides their own
              const p = { ...raw, mine: raw.mine && (book.data?.segregated ?? false) };
              if (p.kind === 'challan') {
                return <PendingChallan key={`ch-${p.entityId}`} challanId={p.entityId} session={session} mine={p.mine}
                                       onDecided={(d) => { setDecided((old) => [...old.filter((x) => x.challanId !== d.challanId), d]); book.reload(); }} />;
              }
              if (p.kind === 'order') return <PendingOrder key={`or-${p.entityId}`} item={p} session={session} onDecided={() => book.reload()} />;
              if (p.kind === 'purchase_order') return <PendingPurchase key={`po-${p.entityId}`} item={p} session={session} onDecided={() => book.reload()} />;
              if (p.kind === 'quotation') return <PendingQuotation key={`q-${p.entityId}`} item={p} session={session} onDecided={() => book.reload()} />;
              return <PendingHold key={`hold-${p.entityId}`} item={p} session={session} onDecided={() => book.reload()} />;
            })}

      {book.data && book.data.decided.length > 0 ? (
        <Card title="Decided" sub="the approvals book, newest first — who asked, who decided, and why">
          <table className="table">
            <thead><tr><th>When</th><th>What</th><th>Decision</th><th>By</th><th>Raised by</th><th>Value</th><th>Reason</th></tr></thead>
            <tbody>
              {book.data.decided.map((d) => (
                <tr key={d.approvalId}>
                  <td>{when(d.decidedAt)}</td>
                  <td>{words(d.kind)} #{d.entityId}</td>
                  <td><Pill tone={d.decision === 'approved' ? 'good' : 'danger'}>{d.decision}</Pill></td>
                  <td>{d.decidedByName ?? `user ${d.decidedBy}`}</td>
                  <td>{d.requestedByName ?? (d.requestedBy ? `user ${d.requestedBy}` : '—')}</td>
                  <td>{d.valuePaise == null ? '—' : <Money paise={d.valuePaise} />}</td>
                  <td className="muted">{d.decisionReason ?? d.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
    </>
  );
}

type PendingItem = Awaited<ReturnType<typeof api.view<'approvals.book'>>>['pending'][number];

function Mine() {
  return <p className="muted" style={{ margin: 0 }}>You raised this yourself, so the decision is somebody else's.</p>;
}

/** One pending challan, in full — the reviewer decides without leaving the list. */
function PendingChallan({ challanId, session, onDecided, mine }: { challanId: number; session: Session; onDecided: (d: Decided) => void; mine?: boolean }) {
  const asked = useAsk(() => api.view('challans.get', { challanId }), [challanId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [decided, setDecided] = useState<string | null>(null);

  if (asked.loading && !asked.data) return <Card><Loading what="the challan" /></Card>;
  if (asked.error) return <Card><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const d = asked.data!;
  const c = d.challan;
  if (decided) {
    return <Card><Banner kind={decided === 'approved' ? 'good' : 'warn'}
                         title={decided === 'approved' ? `Approved — ${c.type === 'dispatch' ? 'the load may depart' : 'the cylinders come back'}.` : 'Rejected — the operator will see why.'} /></Card>;
  }
  if (c.status !== 'pending') return null; // somebody else decided while this was on screen

  async function approve() {
    const ok = await runner.run((retryKey) => api.command('challan.approve', { challanId }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setDecided('approved'); onDecided({ challanId, type: c.type, customerName: c.customerName, action: 'approved' }); }
  }
  async function reject() {
    const ok = await runner.run(() => api.command('challan.reject', { challanId, reason: reason.trim() }));
    if (ok) { setDecided('rejected'); onDecided({ challanId, type: c.type, customerName: c.customerName, action: 'rejected' }); }
  }

  return (
    <Card title={`${c.type === 'dispatch' ? 'Dispatch' : 'Return'} · ${c.customerName}`}
          sub={`${c.siteName} · ${date(c.eventDate)} · waited ${daysAgo(c.eventDate)} · ${count(c.lineCount)} cylinder${c.lineCount === 1 ? '' : 's'}${c.createdBy ? ` · by user ${c.createdBy}` : ''}`}
          actions={<>
            <a href={`#/challans/${c.challanId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/challans/${c.challanId}`); }}>on the register</a>
            {can(session, 'challan.approve') && !mine ? <>
              <Button kind="danger" onClick={() => setRejecting((v) => !v)}>{rejecting ? 'Keep it' : 'Reject'}</Button>
              <Button kind="primary" onClick={() => { void approve(); }} disabled={runner.busy}>{runner.busy ? 'Approving…' : 'Approve & apply'}</Button>
            </> : null}
          </>}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}
      {mine ? <Mine /> : null}
      {c.withoutOrderReason ? <Banner kind="warn" title={`No order behind it — ${words(c.withoutOrderReason)}.`} {...(c.withoutOrderNote ? { detail: c.withoutOrderNote } : {})} /> : null}
      {rejecting ? (
        <div style={{ marginBottom: 12 }}>
          <TextArea name={`why-${c.challanId}`} label="Why it is rejected" value={reason} onChange={setReason} />
          <Button kind="danger" onClick={() => { void reject(); }} disabled={runner.busy || !reason.trim()}>Reject the challan</Button>
        </div>
      ) : null}
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {d.lines.map((l: { lineId: number; internalId: string; serialNo: string; gasService: string }) => (
          <li key={l.lineId}><Serial>{l.internalId}</Serial> <span className="muted">· {l.serialNo} · {words(l.gasService)}</span></li>
        ))}
      </ul>
      {d.pendingCharges.length ? (
        <p className="muted" style={{ marginBottom: 0 }}>with {count(d.pendingCharges.length)} charge{d.pendingCharges.length === 1 ? '' : 's'} added while checking — they reach the memo</p>
      ) : null}
    </Card>
  );
}

/** One draft order awaiting approval. */
function PendingOrder({ item, session, onDecided }: { item: PendingItem; session: Session; onDecided: () => void }) {
  const runner = useRun(MESSAGES);
  const [decided, setDecided] = useState(false);
  async function approve() {
    const ok = await runner.run(() => api.command('order.approve', { orderId: item.entityId }));
    if (ok) { setDecided(true); onDecided(); }
  }
  if (decided) return <Card><Banner kind="good" title="Approved — the yard may prepare against it." /></Card>;
  return (
    <Card title={item.label} sub={`${item.note ?? ''}${item.requestedByName ? ` · raised by ${item.requestedByName}` : ''}${item.requestedOn ? ` · ${date(item.requestedOn)}` : ''}`}
          actions={<>
            <a href={item.route} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(item.route); }}>open the order</a>
            {can(session, 'order.approve') && !item.mine
              ? <Button kind="primary" onClick={() => { void approve(); }} disabled={runner.busy}>{runner.busy ? 'Approving…' : 'Approve'}</Button> : null}
          </>}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {item.mine ? <Mine /> : <p className="muted" style={{ margin: 0 }}>Approving fixes what was agreed; amendments after this are their own recorded versions.</p>}
    </Card>
  );
}

/** One draft purchase order awaiting approval. */
function PendingPurchase({ item, session, onDecided }: { item: PendingItem; session: Session; onDecided: () => void }) {
  const runner = useRun(MESSAGES);
  const [decided, setDecided] = useState(false);
  async function approve() {
    const ok = await runner.run((retryKey) => api.command('po.approve', { purchaseOrderId: item.entityId }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setDecided(true); onDecided(); }
  }
  if (decided) return <Card><Banner kind="good" title="Approved — the purchase order carries its number and may be sent." /></Card>;
  return (
    <Card title={item.label} sub={`${item.valuePaise != null ? money(item.valuePaise) : ''}${item.requestedByName ? ` · raised by ${item.requestedByName}` : ''}${item.requestedOn ? ` · ${date(item.requestedOn)}` : ''}`}
          actions={<>
            <a href={item.route} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(item.route); }}>open the purchase order</a>
            {can(session, 'po.approve') && !item.mine
              ? <Button kind="primary" onClick={() => { void approve(); }} disabled={runner.busy}>{runner.busy ? 'Approving…' : 'Approve'}</Button> : null}
          </>}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {item.mine ? <Mine /> : null}
    </Card>
  );
}

/** One quarantined cylinder: lifting the hold is a decision, and not the recorder's. */
function PendingHold({ item, session, onDecided }: { item: PendingItem; session: Session; onDecided: () => void }) {
  const runner = useRun(MESSAGES);
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(false);
  const [decided, setDecided] = useState(false);
  async function release() {
    const ok = await runner.run((retryKey) => api.command('cylinder.releaseQuarantine', { cylinderId: item.entityId, holdId: item.holdId ?? 0, reason: reason.trim() }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setDecided(true); onDecided(); }
  }
  if (decided) return <Card><Banner kind="good" title="Released — the hold is lifted; the cylinder's other facts still decide whether it may be filled." /></Card>;
  return (
    <Card title={item.label} sub={`${item.note ?? ''}${item.requestedByName ? ` · held by ${item.requestedByName}` : ''}${item.requestedOn ? ` · since ${date(item.requestedOn)}` : ''}`}
          actions={<>
            <a href={item.route} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(item.route); }}>the passport</a>
            {can(session, 'cylinder.release_quarantine') && !item.mine
              ? <Button kind="primary" onClick={() => setOpen((v) => !v)}>{open ? 'Keep it held' : 'Release'}</Button> : null}
          </>}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {item.mine ? <Mine /> : null}
      {open ? (
        <div>
          <TextArea name={`release-${item.entityId}`} label="What makes it safe now" value={reason} onChange={setReason} />
          <Button kind="primary" onClick={() => { void release(); }} disabled={runner.busy || !reason.trim()}>Lift the quarantine</Button>
        </div>
      ) : null}
    </Card>
  );
}

/** 0174 — a quotation awaiting approval: approving writes the customer's card; rejecting sends it back with the reason. */
function PendingQuotation({ item, session, onDecided }: { item: PendingItem; session: Session; onDecided: () => void }) {
  const runner = useRun(MESSAGES);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [decided, setDecided] = useState<string | null>(null);
  async function decide(decision: 'approved' | 'rejected') {
    const ok = await runner.run((retryKey) => api.command('quote.approve', { quotationId: item.entityId, decision, ...(reason.trim() ? { reason: reason.trim() } : {}) }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setDecided(decision); onDecided(); }
  }
  if (decided) return <Card><Banner kind={decided === 'approved' ? 'good' : 'warn'} title={decided === 'approved' ? 'Approved — the customer’s card carries these rates; issue it to send the offer.' : 'Rejected — it is back in draft with your reason.'} /></Card>;
  return (
    <Card title={item.label} sub={`${item.valuePaise != null ? money(item.valuePaise) : ''}${item.note ? ` · ${item.note}` : ''}${item.requestedByName ? ` · raised by ${item.requestedByName}` : ''}${item.requestedOn ? ` · ${date(item.requestedOn)}` : ''}`}
          actions={<>
            <a href={item.route} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(item.route); }}>open the quotation</a>
            {can(session, 'quote.approve') && !item.mine ? <>
              <Button kind="danger" onClick={() => setRejecting((v) => !v)}>{rejecting ? 'Keep it' : 'Reject'}</Button>
              <Button kind="primary" onClick={() => { void decide('approved'); }} disabled={runner.busy}>{runner.busy ? 'Approving…' : 'Approve'}</Button>
            </> : null}
          </>}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {item.mine ? <Mine /> : null}
      {rejecting ? (<div><TextArea name={`qwhy-${item.entityId}`} label="Why it is rejected" value={reason} onChange={setReason} /><Button kind="danger" onClick={() => { void decide('rejected'); }} disabled={runner.busy || !reason.trim()}>Reject the quotation</Button></div>) : null}
    </Card>
  );
}
